// QuotationManagement.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { apiURL } from "../config/config";
import LoadingOverlay from "../components/LoadingOverlay";
import PaginationControls from "../components/PaginationControls";
import SearchBar from "../components/SearchBar";
import QuotationManagementTableDesktop from "../components/QuotationManagementTableDesktop";
import QuotationManagementTableMobile from "../components/QuotationManagementTableMobile";
import BackButton from "../components/BackButton";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { FiFileText } from "react-icons/fi";
import { quotationMatchesSearch } from "../utils/quotationSearch";
import {
  getQuotationCacheYear,
  getCachedQuotations,
  patchCachedQuotations,
  setCachedQuotations,
  upsertCachedQuotation,
} from "../utils/quotationCache";

const QuotationManagement = () => {
  const [allQuotations, setAllQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(true); // โหลดข้อมูล
  const [duplicateLoading, setDuplicateLoading] = useState(false); // โหลดตอน duplicate
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotations(selectedYear);
  }, [selectedYear]);

  const fetchQuotations = async (year) => {
    const cachedQuotations = getCachedQuotations(year);
    if (cachedQuotations) {
      setAllQuotations(cachedQuotations);
      setLoading(false);
    }

    setLoading(!cachedQuotations);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${apiURL}quotations?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllQuotations(res.data || []);
      setCachedQuotations(year, res.data || []);
    } catch (error) {
      if (!cachedQuotations) {
        toast.error("Failed to fetch quotations");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let result = allQuotations;

    if (searchTerm) {
      result = result.filter((q) => quotationMatchesSearch(q, searchTerm));
    }

    const total = Math.ceil(result.length / pageSize) || 1;
    const start = (currentPage - 1) * pageSize;
    const paginated = result.slice(start, start + pageSize);

    setFilteredQuotations(paginated);
    setTotalPages(total);
  }, [searchTerm, currentPage, allQuotations]);

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }

    const sorted = [...filteredQuotations].sort((a, b) => {
      const aVal = a[key] || "";
      const bVal = b[key] || "";
      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setFilteredQuotations(sorted);
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? "↑" : "↓";
    }
    return "";
  };

  const handleDelete = async (quotation) => {
    const result = await Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `คุณต้องการลบใบเสนอราคา QT-(${quotation.type})-${quotation.runNumber} นี้ใช่หรือไม่?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "ใช่, ลบเลย!",
      cancelButtonText: "ยกเลิก",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${apiURL}quotations/${quotation._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllQuotations((prev) => prev.filter((q) => q._id !== quotation._id));
        patchCachedQuotations(selectedYear, (prev) =>
          prev.filter((q) => q._id !== quotation._id),
        );
        Swal.fire("ลบสำเร็จ!", "ใบเสนอราคาได้ถูกลบแล้ว", "success");
      } catch (error) {
        Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถลบใบเสนอราคาได้", "error");
      }
    }
  };

  const handleDuplicate = async (quotation) => {
    const result = await Swal.fire({
      title: "Duplicate Quotation?",
      text: `คุณต้องการคัดลอก QT-(${quotation.type})-${quotation.runNumber} ใช่หรือไม่?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, duplicate!",
    });

    if (result.isConfirmed) {
      try {
        setDuplicateLoading(true);
        const token = localStorage.getItem("token");
        const res = await axios.post(
          `${apiURL}quotations/${quotation._id}/duplicate`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const quotationId = res.data?._id;
        if (!quotationId) {
          throw new Error("Duplicated quotation ID is missing");
        }

        const quotationResponse = await axios.get(
          `${apiURL}quotations/${quotationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const newQT = quotationResponse.data;
        upsertCachedQuotation(newQT);

        if (String(getQuotationCacheYear(newQT)) === String(selectedYear)) {
          setAllQuotations((prev) => [
            newQT,
            ...prev.filter((q) => q._id !== newQT._id),
          ]);
        }

        Swal.fire("Success!", "Quotation duplicated successfully", "success");

        navigate(`/quotations/edit/${quotationId}`);
      } catch (error) {
        Swal.fire("Error!", "Duplicate failed", "error");
      } finally {
        setDuplicateLoading(false);
      }
    }
  };

  return (
    <div className="px-4 sm:px-8 py-5 sm:py-6 relative dark:bg-gray-950 min-h-screen transition-colors duration-300">
      {/* Overlay โหลดข้อมูล */}
      <LoadingOverlay loading={loading || duplicateLoading} />

      {/* Header Section */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-between mb-2">
          <BackButton label="ย้อนกลับ" />
        </div>

        {/* Title Section */}
        <div>
          <h1 className="text-xl sm:text-[26px] font-semibold text-gray-900 dark:text-yellow-400 leading-tight transition-colors">
            Quotation Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5 sm:mt-1 transition-colors">
            จัดการใบเสนอราคา สร้าง แก้ไข และตรวจสอบสถานะได้ที่นี่
          </p>
        </div>
      </div>

      {/* Filter + Search + Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
        <div className="flex flex-1 items-center gap-2 sm:gap-3">
          {/* Year Selector */}
          <select
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200
             rounded-md px-3 py-2 text-sm w-[90px] sm:w-24 focus:ring-2 focus:ring-indigo-400 dark:focus:ring-indigo-500
             focus:border-indigo-400 dark:focus:border-indigo-600 transition-all duration-200"
          >
            {[
              currentYear + 1,
              currentYear,
              currentYear - 1,
              currentYear - 2,
            ].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          {/* Search Bar */}
          <div className="flex-1">
            <SearchBar
              searchTerm={searchTerm}
              setSearchTerm={(value) => {
                setSearchTerm(value);
                setCurrentPage(1);
              }}
              placeholder="ค้นหาใบเสนอราคา..."
            />
          </div>
        </div>

        {/* Create Quotation Button */}
        <button
          onClick={() => navigate("/quotations/create")}
          className="w-full sm:w-auto flex items-center justify-center gap-2 
                   bg-indigo-500 hover:bg-indigo-600 dark:bg-indigo-600 dark:hover:bg-indigo-700
                   text-white px-5 py-2.5 text-sm font-medium rounded-md shadow-sm 
                   transition-all duration-200"
        >
          <span className="text-base leading-none">＋</span>
          <span>สร้างใบเสนอราคา</span>
        </button>
      </div>

      {/* Table Section */}
      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-4 sm:p-5 transition-colors duration-300">
        <div className="flex items-center gap-2 text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 sm:mb-4 transition-colors">
          <FiFileText className="text-gray-500 dark:text-indigo-400" />
          <span>Quotations</span>
        </div>

        {/* Desktop Table */}
        <QuotationManagementTableDesktop
          quotations={filteredQuotations}
          handleSort={handleSort}
          getSortIndicator={getSortIndicator}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
        />

        {/* Mobile Table */}
        <QuotationManagementTableMobile
          quotations={filteredQuotations}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
        />

        {/* Pagination */}
        <div className="mt-5 sm:mt-6">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </div>
  );
};

export default QuotationManagement;
